import React, { useEffect, useState } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchProductTypes } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const PRODUCT_CLASS_LABELS = {
  Goods: "productType.class.goods",
  PersonalService: "productType.class.personalService",
  ApplicationService: "productType.class.applicationService",
};

export default function ProductTypeListPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const loadData = async () => {
    setLoading(true);
    try { setRows(await fetchProductTypes(businessDomainId)); }
    catch (e) { notify.apiError(e); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [businessDomainId]);

  const filteredRows = searchText
    ? rows.filter((r) => r.productTypeName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "productTypeName", headerName: t("productType.name"), flex: 1, minWidth: 200 },
    {
      field: "productClass",
      headerName: t("productType.productClass"),
      width: 200,
      valueFormatter: (value) => {
        const key = PRODUCT_CLASS_LABELS[value];
        return key ? t(key) : value || "";
      },
    },
  ];

  const handleRowClick = (params) => {
    navigate(`/design/${businessDomainId}/product-types/${params.row.productTypeId}`);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("productType.list.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/design/${businessDomainId}/product-types/create`)}>{t("common.add")}</Button>
        </Box>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.productTypeId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        sx={{
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
        }}
      />
    </Box>
  );
}
